import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import Dashboard from './pages/Dashboard';
import WattelzcePage from './pages/WattelzcePage';
import WattelzceDemoPage from './pages/WattelzceDemoPage';
import WattelEMPage from './pages/WattelEMPage';
import AlertSettingsPage from './pages/AlertSettingsPage';
import { BuildingArchitecturePage } from './pages/BuildingArchitecturePage';
import WifiProvisioningPage from './features/wifi-provisioning/WifiProvisioningPage';
import LoginPage from './pages/LoginPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import MembersPage from './pages/MembersPage';
import SignupPage from './pages/SignupPage';
import AcceptInvitationPage from './pages/AcceptInvitationPage';
import AdminSignupRequestsPage from './pages/AdminSignupRequestsPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import MyClientsPage from './pages/MyClientsPage';
import ClientDashboardView from './pages/ClientDashboardView';
import MyInstallersPage from './pages/MyInstallersPage';
import AdminPage from './pages/AdminPage';
import AdminInstallersPage from './pages/AdminInstallersPage';
import AdminClientsPage from './pages/AdminClientsPage';
import AdminSupportLogsPage from './pages/AdminSupportLogsPage';
import AdminDeploymentsPage from './pages/AdminDeploymentsPage';
import MyInfraPage from './pages/MyInfraPage';
import OtaPage from './pages/OtaPage';
import AdminDeviceModelsPage from './pages/AdminDeviceModelsPage';
import BornesPage from './pages/BornesPage';
import RapportRSEPage from './pages/RapportRSEPage';
import { useAuth } from './context/AuthContext';

// Redirect installers to /my-clients, others to Dashboard
function IndexRedirect() {
  const { currentMembership } = useAuth();
  const role = currentMembership?.role;
  if (role === 'installer_admin' || role === 'installer_technician') {
    return <Navigate to="/my-clients" replace />;
  }
  return <Dashboard />;
}

function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/invitations/:token" element={<AcceptInvitationPage />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      {/* Protected routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<IndexRedirect />} />
        
        {/* Wattelzce - requires wattelzce or admin role */}
        <Route path="wattelzce" element={
          <ProtectedRoute requiredAccess="wattelzce">
            <WattelzcePage />
          </ProtectedRoute>
        } />
        
        {/* Wattelzce Demo - requires wattelzce or admin role */}
        <Route path="wattelzce-demo" element={
          <ProtectedRoute requiredAccess="wattelzce">
            <WattelzceDemoPage />
          </ProtectedRoute>
        } />
        
        {/* WattelEM - requires wattelzce or admin role */}
        <Route path="wattel-em" element={
          <ProtectedRoute requiredAccess="wattelzce">
            <WattelEMPage />
          </ProtectedRoute>
        } />
        
        {/* Seuils d'alerte - requires wattelzce or admin role */}
        <Route path="alertes/seuils" element={
          <ProtectedRoute requiredAccess="wattelzce">
            <AlertSettingsPage />
          </ProtectedRoute>
        } />

        {/* Building Architecture - requires wattelzce or admin role */}
        <Route path="building-architecture" element={
          <ProtectedRoute requiredAccess="wattelzce">
            <BuildingArchitecturePage />
          </ProtectedRoute>
        } />


        {/* OTA Firmware Updates - super_admin uniquement */}
        <Route path="ota" element={
          <ProtectedRoute requiredAccess="wattelzce" requireSuperAdmin>
            <OtaPage />
          </ProtectedRoute>
        } />

        {/* WiFi Provisioning (appairage borne) - admin/maintenance/installer */}
        <Route path="wifi-provisioning" element={
          <ProtectedRoute requiredAccess="wattelzce" requiredOrgRoles={['org_admin', 'maintenance', 'installer_admin', 'installer_technician']}>
            <WifiProvisioningPage />
          </ProtectedRoute>
        } />

        {/* Bornes - Gestion des bornes de recharge */}
        <Route path="bornes" element={
          <ProtectedRoute requiredAccess="wattelzce">
            <BornesPage />
          </ProtectedRoute>
        } />

        {/* Rapport RSE - Décret Tertiaire */}
        <Route path="rapport-rse" element={
          <ProtectedRoute requiredAccess="wattelzce">
            <RapportRSEPage />
          </ProtectedRoute>
        } />

        {/* Members management - org_admin or installer_admin */}
        <Route path="members" element={
          <ProtectedRoute requiredOrgRoles={['org_admin', 'installer_admin']} denySuperAdmin>
            <MembersPage />
          </ProtectedRoute>
        } />

        {/* Super-admin: old route redirect */}
        <Route path="admin/signup-requests" element={<Navigate to="/admin/signup-requests" replace />} />

        {/* ─── Multi-tenant architecture routes ─── */}

        {/* Installer B2: my clients grid */}
        <Route path="my-clients" element={
          <ProtectedRoute requiredOrgRoles={['installer_admin', 'installer_technician']}>
            <MyClientsPage />
          </ProtectedRoute>
        } />

        {/* Installer B2: support view of a specific client */}
        <Route path="clients/:orgId/dashboard" element={
          <ProtectedRoute requiredOrgRoles={['installer_admin', 'installer_technician']}>
            <ClientDashboardView />
          </ProtectedRoute>
        } />

        {/* Org admin : modèles d'appareils de son organisation (éditeur d'architecture) */}
        <Route path="device-models" element={
          <ProtectedRoute requiredAccess="wattelzce" requiredOrgRoles={['org_admin']} denySuperAdmin>
            <AdminDeviceModelsPage mode="org" />
          </ProtectedRoute>
        } />

        {/* Client B3: my installers management */}
        <Route path="my-installers" element={
          <ProtectedRoute requiredOrgRoles={['org_admin']}>
            <MyInstallersPage />
          </ProtectedRoute>
        } />
      </Route>

      {/* ─── Admin ZCE — layout dédié avec sidebar ─── */}
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPage />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/admin/installers" replace />} />
        <Route path="installers" element={<AdminInstallersPage />} />
        <Route path="clients" element={<AdminClientsPage />} />
        <Route path="support-logs" element={<AdminSupportLogsPage />} />
        <Route path="signup-requests" element={<AdminSignupRequestsPage />} />
        {/* Déploiements d'instances client Scaleway (super_admin ; masqué sur instance client) */}
        {import.meta.env.VITE_IS_CLIENT_INSTANCE !== 'true' && (
          <Route path="deployments" element={
            <ProtectedRoute requireSuperAdmin>
              <AdminDeploymentsPage />
            </ProtectedRoute>
          } />
        )}
        {/* Pilotage de l'infra sur une instance client dédiée */}
        {import.meta.env.VITE_IS_CLIENT_INSTANCE === 'true' && (
          <Route path="my-infra" element={
            <ProtectedRoute requireSuperAdmin>
              <MyInfraPage />
            </ProtectedRoute>
          } />
        )}
        {/* OTA dans la console super-admin (même layout, ne sort pas de la console) */}
        <Route path="ota" element={
          <ProtectedRoute requireSuperAdmin>
            <OtaPage />
          </ProtectedRoute>
        } />
        {/* Catalogue des modèles d'appareils de l'éditeur d'architecture */}
        <Route path="device-models" element={
          <ProtectedRoute requireSuperAdmin>
            <AdminDeviceModelsPage />
          </ProtectedRoute>
        } />
      </Route>
    </Routes>
  );
}

export default App;
